import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PageShellProps {
  /** Lucide icon shown next to the subtitle in the header row. */
  Icon?: LucideIcon;
  /**
   * One-sentence subtitle. Optional — omit when the AdminLayout header
   * already conveys enough context (page name + icon are shown there).
   */
  subtitle?: string;
  /** Right-aligned action(s) in the header — typically a `Button`. */
  actions?: React.ReactNode;
  /** Tab bar — pass `<UnderlineTabs ... />`. Optional for single-tab pages. */
  tabs?: React.ReactNode;
  /** Content area. Should follow the `flex min-h-[calc(100vh-240px)]` pattern. */
  children: React.ReactNode;
  className?: string;
}

/**
 * Page shell — header (icon + subtitle + actions), optional tab bar, and
 * a content slot. Wraps the page's contents in the canonical V2 outer
 * `space-y-4` container.
 *
 * Composes with `<UnderlineTabs>` (tabs prop) and `<DragResizeSidePanel>`
 * (inside `children`).
 */
export function PageShell({ Icon, subtitle, actions, tabs, children, className }: PageShellProps) {
  // The AdminLayout dark header already shows the page name + icon, so the
  // subtitle row is rendered only when there is something to add (subtitle or
  // actions). Pages with neither skip the row entirely (mirrors Rocks).
  const hasHeader = !!subtitle || !!actions;
  return (
    <div className={cn('space-y-4', className)}>
      {hasHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {subtitle && Icon && <Icon className="h-5 w-5 text-muted-foreground shrink-0" />}
            {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      )}
      {tabs}
      {children}
    </div>
  );
}
